import { getQueryParamCaseInsensitive } from '@/features/shared/queryParams';
import type { FilterIncidentsBody } from './incidentsApi';

export type IncidentArchiveFilters = {
  from: Date | null;
  to: Date | null;
  phoneNumber: string | null;
  callerName: string | null;
  note: string | null;
};

function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function addDays(date: Date, days: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

export function defaultIncidentArchiveFilters(now: Date = new Date()): IncidentArchiveFilters {
  return {
    from: startOfDay(addDays(now, -1)),
    to: startOfDay(now),
    phoneNumber: null,
    callerName: null,
    note: null,
  };
}

export function sanitizeFilters(filters: IncidentArchiveFilters): IncidentArchiveFilters {
  const clean = (value: string | null): string | null => {
    if (value == null || value.trim() === '') {
      return null;
    }
    return value.trim();
  };

  return {
    ...filters,
    phoneNumber: clean(filters.phoneNumber),
    callerName: clean(filters.callerName),
    note: clean(filters.note),
  };
}

const pad = (value: number, width = 2): string => String(value).padStart(width, '0');

export function toSortableDateTime(date: Date): string {
  return (
    `${String(date.getFullYear()).padStart(4, '0')}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
  );
}

const LOCAL_DATE_TIME_PATTERN =
  /^(\d{4})-(\d{1,2})-(\d{1,2})(?:[T ](\d{1,2}):(\d{1,2})(?::(\d{1,2})(?:\.(\d+))?)?)?$/;

export function parseDotNetDate(value: string): Date | null {
  const match = LOCAL_DATE_TIME_PATTERN.exec(value.trim());
  if (match) {
    const [, year, month, day, hours, minutes, seconds, fraction] = match;
    const date = new Date(
      Number(year),
      Number(month) - 1,
      Number(day),
      Number(hours ?? '0'),
      Number(minutes ?? '0'),
      Number(seconds ?? '0'),
      fraction != null ? Math.round(Number(`0.${fraction}`) * 1000) : 0,
    );
    const valid =
      date.getFullYear() === Number(year) &&
      date.getMonth() === Number(month) - 1 &&
      date.getDate() === Number(day);
    return valid ? date : null;
  }

  const fallback = new Date(value);
  return Number.isNaN(fallback.getTime()) ? null : fallback;
}

export function parseFiltersFromSearch(
  search: string,
  now: Date = new Date(),
): IncidentArchiveFilters {
  const defaults = defaultIncidentArchiveFilters(now);
  const params = new URLSearchParams(search);

  const get = (name: string): string | null => getQueryParamCaseInsensitive(params, name);

  const getDate = (name: string): Date | null => {
    const raw = get(name);
    return raw == null ? null : parseDotNetDate(raw);
  };

  return {
    callerName: get('callerName') ?? defaults.callerName,
    phoneNumber: get('phoneNumber') ?? defaults.phoneNumber,
    from: getDate('from') ?? defaults.from,
    to: getDate('to') ?? defaults.to,
    note: get('note') ?? defaults.note,
  };
}

function encodeQueryComponent(value: string): string {
  return encodeURIComponent(value).replace(
    /[!'()*]/g,
    (char) => `%${char.charCodeAt(0).toString(16).toUpperCase()}`,
  );
}

export function applyFiltersToSearch(search: string, filters: IncidentArchiveFilters): string {
  const query = new Map<string, string[]>();
  for (const [key, value] of new URLSearchParams(search)) {
    const existing = query.get(key);
    if (existing) {
      existing.push(value);
    } else {
      query.set(key, [value]);
    }
  }

  const values: [string, string | null][] = [
    ['from', filters.from != null ? toSortableDateTime(filters.from) : null],
    ['to', filters.to != null ? toSortableDateTime(filters.to) : null],
    ['phoneNumber', filters.phoneNumber],
    ['callerName', filters.callerName],
    ['note', filters.note],
  ];

  for (const [key, value] of values) {
    if (value != null) {
      query.set(key, [value]);
    } else {
      query.delete(key);
    }
  }

  const pairs: string[] = [];
  for (const [key, keyValues] of query) {
    for (const value of keyValues) {
      pairs.push(`${encodeQueryComponent(key)}=${encodeQueryComponent(value)}`);
    }
  }

  return pairs.length > 0 ? `?${pairs.join('&')}` : '';
}

export function buildFilterIncidentsBody(filters: IncidentArchiveFilters): FilterIncidentsBody {
  const from = filters.from != null ? toSortableDateTime(startOfDay(filters.from)) : null;
  const to = filters.to != null ? toSortableDateTime(addDays(startOfDay(filters.to), 1)) : null;

  return {
    from,
    to,
    callerFullname: filters.callerName,
    phoneNumber: filters.phoneNumber,
    dispatcherNote: filters.note,
  };
}
